import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Phone, ChevronRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { client } from "@/lib/api";

const emptyCustomer = {
  name: "",
  code: "",
  main_contact: "",
  phone: "",
  email: "",
  qb_name: "",
  qb_id: "",
  notes: "",
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const load = async (term = query) => {
    try {
      const response = await client.get("/customers", { params: { q: term } });
      setCustomers(response.data);
    } catch {
      toast.error("Could not load customers");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => load(query), 180);
    return () => clearTimeout(timeout);
  }, [query]);

  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const nameKey = value => value.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/).filter(word => word && !["ltd", "limited", "inc", "incorporated", "llc"].includes(word)).join(" ");

  const similar = form.name.trim()
    ? customers.find(item => item.name.toLowerCase() !== form.name.trim().toLowerCase()
        && nameKey(item.name) === nameKey(form.name.trim()))
    : null;

  const submit = async event => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    try {
      setSaving(true);
      const response = await client.post("/customers", form);
      setLastCreated(response.data);
      toast.success(`${response.data.name} added as ${response.data.code}`);
      setForm(emptyCustomer);
      setShowForm(false);
      await load("");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not add customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="customers-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5F6B7A" }}>
            Customer directory
          </div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Customers</h1>
          <p className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>
            Search customer codes, contacts, phones, and equipment serials.
          </p>
        </div>
        <Button
          className="h-9 px-4 bf-btn-primary"
          onClick={() => setShowForm(value => !value)}
          data-testid="new-customer-button"
        >
          <Plus size={15} className="mr-2" />
          New customer
        </Button>
      </div>

      {showForm && (
        <form
          className="mt-6 bf-card"
          style={{ padding: "20px" }}
          onSubmit={submit}
          data-testid="new-customer-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Field label="Customer name" required>
              <Input
                value={form.name}
                onChange={event => change("name", event.target.value)}
                data-testid="customer-name-input"
              />
            </Field>
            <Field label="Short code">
              <Input
                value={form.code}
                onChange={event => change("code", event.target.value.toUpperCase())}
                placeholder="Left blank, we assign one"
                className="mono"
                data-testid="customer-code-input"
              />
            </Field>
            <Field label="Main contact">
              <Input
                value={form.main_contact}
                onChange={event => change("main_contact", event.target.value)}
                data-testid="customer-contact-input"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={event => change("phone", event.target.value)}
                inputMode="tel"
                data-testid="customer-phone-input"
              />
            </Field>
            <Field label="Email">
              <Input
                value={form.email}
                onChange={event => change("email", event.target.value)}
                type="email"
                data-testid="customer-email-input"
              />
            </Field>
            <Field label="QuickBooks name or ID">
              <Input
                value={form.qb_name}
                onChange={event => change("qb_name", event.target.value)}
                data-testid="customer-quickbooks-name-input"
              />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Notes">
              <Textarea
                value={form.notes}
                onChange={event => change("notes", event.target.value)}
                rows={3}
                data-testid="customer-notes-input"
              />
            </Field>
          </div>
          {similar && (
            <div className="mt-4 rounded-[10px] px-3 py-2.5 text-[13.5px]" data-testid="similar-customer-warning"
                 style={{ background: "#FFF6E5", color: "#96650A", lineHeight: 1.5 }}>
              A customer with a similar name already exists: {similar.name} ({similar.code}). Save
              anyway if this is a different business.
            </div>
          )}
          <div className="mt-5 flex gap-2">
            <Button
              className="bf-btn-primary px-4"
              disabled={saving}
              data-testid="save-customer-button"
            >
              {saving ? "Saving…" : "Save customer"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="bf-btn-ghost"
              onClick={() => setShowForm(false)}
              data-testid="cancel-customer-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {lastCreated && (
        <div className="mt-6 bf-card flex items-center gap-3" style={{ padding: "16px" }}
             data-testid="created-customer-code">
          <div className="text-[13.5px]" style={{ lineHeight: 1.5 }}>
            <strong>{lastCreated.name}</strong> saved. Their code is{" "}
            <span className="mono font-semibold">{lastCreated.code}</span> — quote this to them.
          </div>
          <button className="ml-auto text-[13px] underline" onClick={() => setLastCreated(null)}
                  data-testid="dismiss-created-code">Dismiss</button>
        </div>
      )}

      <div className="mt-7 flex items-center gap-2 max-w-[480px]">
        <Search size={16} color="#5B6472" />
        <Input
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder="Name, code, phone, make, model, or serial"
          data-testid="customer-search-input"
        />
      </div>

      <div className="mt-4 bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }}>
        <table className="w-full text-[13.5px]" data-testid="customers-table">
          <thead>
            <tr style={{ background: "#F7F8FA" }}>
              <th className="th-condensed text-left px-4 py-2">Customer</th>
              <th className="th-condensed text-left px-3 py-2">Code</th>
              <th className="th-condensed text-left px-3 py-2">Contact</th>
              <th className="th-condensed text-left px-3 py-2">Phone</th>
              <th className="th-condensed text-right px-3 py-2">Open</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr
                key={customer.id}
                className="border-t hover:bg-[#F6F7F9]"
                style={{ borderColor: "#EFF1F4" }}
                data-testid={`customer-row-${customer.code}`}
              >
                <td className="px-4 py-3 font-medium">{customer.name}</td>
                <td className="px-3 py-3 mono text-[12px]">{customer.code}</td>
                <td className="px-3 py-3">{customer.main_contact || "—"}</td>
                <td className="px-3 py-3">
                  {customer.phone ? (
                    <a
                      href={`tel:${customer.phone}`}
                      className="inline-flex items-center gap-1"
                      style={{ color: "#2563EB" }}
                      data-testid={`customer-phone-link-${customer.code}`}
                    >
                      <Phone size={12} />
                      {customer.phone}
                    </a>
                  ) : "—"}
                </td>
                <td className="px-3 py-3 text-right">
                  <Link
                    to={`/customers/${customer.id}`}
                    className="inline-flex items-center gap-1"
                    style={{ color: "#2563EB" }}
                    data-testid={`open-customer-${customer.code}`}
                  >
                    View <ChevronRight size={14} />
                  </Link>
                </td>
              </tr>
            ))}
            {!customers.length && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-[13px]" style={{ color: "#94A3B8" }}>
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        {label} {required && <span style={{ color: "#B42318" }}>*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}